import { Close } from '@mui/icons-material';
import { Box, Button, IconButton, MenuItem, Modal, Select, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { DocumentDownload } from 'iconsax-react';
import firstItem from 'lodash/first';
import LogRocket from 'logrocket';
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { SlabContext } from '@core/context/SlabContext';
import Enums from '@core/types/api/Enums';
import { NewPdfTemplate, type PdfTemplate } from '@core/types/api/PdfTemplate/PdfTemplate';
import type { Quote } from '@core/types/api/Quote/Quote';
import { type Base64, binaryStringToBase64 } from '@core/utils/base64';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import { useSlabAuth } from '@web/hooks/useSlabAuth';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import { buildPdf } from '../../../pdf/buildPdf';

// Used for admins when tenant has 0 pdf templates - shows raw JSON input for debugging
const adminJsonTemplate = NewPdfTemplate({
  type: Enums.PdfTemplateType.Code,
  template: 'json',
  name: '(Slabstack Admin only:) Input JSON for PDF Generator',
});

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '80vw',
  maxWidth: '90vw',
  height: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  overflowY: 'scroll',
};

export type QuotePdfModalContentProps = {
  quote: Quote;
  requiresQuoteApprovals: boolean;
  /** Called with the rendered PDF whenever the selected template produces one. */
  onPdfChanged?: (pdfBase64: Base64) => void;
};

export const QuotePdfModalContent = ({
  quote,
  onPdfChanged,
  requiresQuoteApprovals,
}: QuotePdfModalContentProps): ReactElement => {
  const [pdfTemplate, setPdfTemplate] = useState<PdfTemplate | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [pdfIsLoadingSpinner, setPdfIsLoadingSpinner] = useState(true);
  const [hasPdfError, setHasPdfError] = useState(false);

  const ctx = useContext(SlabContext);
  const { locale } = ctx.userInfo.tenant;

  // We must drop down to useSlabAuth instead of using SlabContext as usual,
  // because we need to offer the admin-scoped "input JSON" option to admins
  // when they are impersonating non-admins.
  const { user } = useSlabAuth();
  const isSlabstackAdmin = user.admin;

  const {
    data: pdfTemplateList,
    isLoading: isTemplateLoading,
    isError: isTemplateError,
  } = useSlabQuery('GET pdf templates', {
    queryParams: {
      sortBy: {
        name: 'position',
        direction: Enums.SortDirection.Ascending,
      },
      filterBy: [
        {
          name: 'locationId',
          operation: Enums.FilterOperation.EqualsOrNull,
          values: quote.plants.map((p) => p.plant.id),
        },
      ],
      perPage: 0,
      page: 0,
    },
  });
  const {
    data: generatedPdf,
    isLoading: isPdfLoading,
    isError: isPdfError,
  } = useSlabQuery(
    'GET quote pdf by quote ID and template ID',
    { pathParams: { quoteID: quote.id, templateID: pdfTemplate?.id ?? '' } },
    {
      enabled: pdfTemplate?.type === Enums.PdfTemplateType.PdfGenerator,
      // Don't automatically reload PDFs, because that's unexpected for users
      // and also unnecessarily burns PDF generator quota.
      staleTime: Infinity,
    },
  );
  const {
    data: pdfInput,
    isLoading: isPDFInputLoading,
    isError: isPDFInputError,
  } = useSlabQuery(
    'GET quote pdf input json by quote ID',
    { pathParams: { quoteID: quote.id } },
    {
      enabled:
        pdfTemplate?.type === Enums.PdfTemplateType.Code &&
        pdfTemplate?.template === 'json' &&
        isSlabstackAdmin,
    },
  );

  // Once list is loaded, set the pdfTemplate to the first value in the list.
  // For admins with no templates, default to the JSON viewer.
  useEffect(() => {
    if (pdfTemplateList === undefined) {
      return;
    }

    const firstTemplate = firstItem(pdfTemplateList.items);
    if (firstTemplate !== undefined) {
      setPdfTemplate(firstTemplate);
      return;
    }

    if (isSlabstackAdmin) {
      // Admin with no templates - default to JSON viewer
      setPdfTemplate(adminJsonTemplate);
    }
    // Non-admin with no templates: leave pdfTemplate as null, will show graceful message
  }, [pdfTemplateList, isSlabstackAdmin]);

  // Whenever the pdfTemplate changes, this will rebuild the PDF.
  useEffect(() => {
    if (pdfTemplateList === undefined) {
      return undefined;
    }

    setPdfIsLoadingSpinner(true);
    if (pdfTemplate === null) {
      // nothing to do yet
      return undefined;
    }
    onPdfChanged?.('');
    setHasPdfError(false);

    const showErrorDoc = (): void => {
      const errorDoc = '<div>Error generating quote PDF</div>';
      setPdfUrl(`data:text/html,${encodeURIComponent(errorDoc)}`);
      setHasPdfError(true);
      setPdfIsLoadingSpinner(false);
    };

    if (isPdfError || isPDFInputError) {
      showErrorDoc();
      return undefined;
    }

    if (pdfTemplate.type === Enums.PdfTemplateType.Code) {
      if (isSlabstackAdmin && pdfTemplate.template === 'json') {
        const jsonDoc = `<code><pre>${pdfInput}</pre></code>`;
        setPdfUrl(`data:text/html,${encodeURIComponent(jsonDoc)}`);
        onPdfChanged?.(binaryStringToBase64(jsonDoc));
        setPdfIsLoadingSpinner(false);
        return undefined;
      }

      // react-pdf
      let isStale = false;
      buildPdf(quote, pdfTemplate, locale)
        .then((base64) => {
          if (isStale) {
            return;
          }
          setPdfUrl(
            `data:application/pdf;base64,${base64}${
              requiresQuoteApprovals && !quote.status.policyApprovalsMet() ? '#toolbar=0' : ''
            }`,
          );
          onPdfChanged?.(base64);
          setPdfIsLoadingSpinner(false);
        })
        .catch((e: unknown) => {
          LogRocket.captureException(e as Error);
          Sentry.captureException(e);
          if (!isStale) {
            showErrorDoc();
          }
        });
      return (): void => {
        isStale = true;
      };
    }

    if (pdfTemplate.type === Enums.PdfTemplateType.PdfGenerator) {
      // pdf-generator-api
      if (!isPdfLoading && generatedPdf !== undefined) {
        const base64 = generatedPdf.pdf;
        setPdfUrl(
          `data:application/pdf;base64,${base64}${
            requiresQuoteApprovals && !quote.status.policyApprovalsMet() ? '#toolbar=0' : ''
          }`,
        );
        onPdfChanged?.(base64);
        setPdfIsLoadingSpinner(false);
      }
    }
    return undefined;
  }, [pdfTemplate, generatedPdf, isPdfError, pdfInput, isPDFInputError]);

  if (isTemplateError) {
    return <div>Error generating quote PDF</div>;
  }

  const pdfTemplates = [...(pdfTemplateList?.items ?? [])];
  if (isSlabstackAdmin) {
    pdfTemplates.push(adminJsonTemplate);
  }

  const isPreviewLoading =
    isTemplateLoading || isPdfLoading || isPDFInputLoading || pdfIsLoadingSpinner;

  const renderPdf = (): ReactElement => {
    if (isPreviewLoading) {
      return <LoadingSpinner />;
    }
    if (isPdfError) {
      return <div>ERROR</div>;
    }
    return <iframe title='Quote' width='100%' src={pdfUrl} style={{ flexGrow: 1, border: 0 }} />;
  };

  // When no templates exist, show a graceful message instead of attempting to render
  if (!isTemplateLoading && pdfTemplates.length === 0) {
    return (
      <Box display='flex' flexDirection='column' gap='2rem' flexGrow={1}>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          flexGrow={1}
          gap='1rem'
          sx={{ textAlign: 'center', py: 4 }}
        >
          <Typography variant='h5'>No PDF Templates Available</Typography>
          <Typography variant='body1' color='text.secondary'>
            There are no PDF templates configured for your organization. Please contact support for
            help.
          </Typography>
        </Box>
      </Box>
    );
  }

  const downloadDisabled =
    isPreviewLoading ||
    hasPdfError ||
    (requiresQuoteApprovals && !quote.status.policyApprovalsMet());

  return (
    <Box display='flex' flexDirection='column' gap='1rem' flexGrow={1}>
      <Select
        value={pdfTemplate?.id ?? ''}
        disabled={isTemplateLoading}
        onChange={(e): void => {
          const value = pdfTemplates.find((item) => item.id === e.target.value);
          setPdfTemplate(value ?? null);
        }}
        fullWidth
        // Close outlined box
        sx={{
          legend: {
            display: 'none',
          },
        }}
      >
        {pdfTemplates.map((template): ReactElement => (
          <MenuItem key={template.id} value={template.id}>
            {template.name}
          </MenuItem>
        ))}
      </Select>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Typography variant='h5'>Preview</Typography>
        <a
          href={downloadDisabled ? undefined : pdfUrl}
          target='_blank'
          rel='noopener noreferrer'
          download={quote.fileName()}
          style={{ textDecoration: 'none' }}
          aria-label='download'
        >
          <Button
            disabled={downloadDisabled}
            variant='slab-transparent'
            startIcon={<DocumentDownload variant='TwoTone' />}
          >
            DOWNLOAD PDF
          </Button>
        </a>
      </Box>
      <Box display='flex' flexDirection='column' flexGrow={1} minHeight='90vh' maxHeight='120rem'>
        {renderPdf()}
      </Box>
    </Box>
  );
};

/** The content component plus what it takes to own the surrounding Modal. */
type QuotePdfModalProps = QuotePdfModalContentProps & {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export const QuotePdfModal = ({
  quote,
  isOpen,
  setIsOpen,
  requiresQuoteApprovals,
  onPdfChanged,
}: QuotePdfModalProps): ReactElement => (
  <Modal open={isOpen} onClose={(): void => setIsOpen(false)}>
    <Box sx={style}>
      <Box display='flex' justifyContent='flex-end' alignItems='center'>
        <IconButton aria-label='close' onClick={(): void => setIsOpen(false)}>
          <Close />
        </IconButton>
      </Box>

      <QuotePdfModalContent
        quote={quote}
        requiresQuoteApprovals={requiresQuoteApprovals}
        onPdfChanged={onPdfChanged}
      />
    </Box>
  </Modal>
);
